import React, { useMemo, useState } from 'react';
import type { CostsFiltersPayload, DateKey, EntityType } from '../../../../types/costs';
import { DatePickerPopover } from './DatePickerPopover';

const periodOptions: { label: string; value: 'history' | DateKey }[] = [
    { label: 'All Time', value: 'history' },
    { label: 'Today', value: 'day' },
    { label: 'Prev week', value: 'week' },
    { label: 'Prev month', value: 'month' },
];

const typeOptions: { label: string; value: EntityType | 'all' }[] = [
    { label: 'All', value: 'all' },
    { label: 'Hotels', value: 'hotels' },
    { label: 'Restaurants', value: 'restaurants' },
];

const inputBase =
    'w-full rounded-xl border bg-white dark:bg-black px-3 py-3 text-sm text-black dark:text-white border-gray-200 dark:border-borderColor-darkMode ' +
    'focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-gray-300 dark:focus:border-gray-600';

const selectBase =
    inputBase + ' appearance-none pr-10 !py-3';

const btnBase =
    'rounded-xl text-sm border transition-all focus:outline-none focus:ring-0 focus:ring-offset-0';

const iconWrap =
    'relative';

const rightIcon =
    'absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none';

const ChevronDown = () => (
    <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
        <path
            d='M6 9l6 6 6-6'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
        />
    </svg>
);

type PlatformOption = {
    label: string;
    value: string;
    name?: string;
    icon?: string;
};

const platformBtn =
    inputBase + ' flex items-center justify-between gap-3 !py-3';

const menuBase =
    'absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-borderColor-darkMode dark:bg-black';

const menuItemBase =
    'w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-white/5';

const menuItemActive =
    'bg-gray-50 dark:bg-white/5';

const PlatformSelect = ({
    value,
    options,
    onChange,
}: {
    value?: string;
    options: PlatformOption[];
    onChange: (next?: string) => void;
}) => {
    const [open, setOpen] = useState(false);

    const selected = useMemo(() => {
        if (!value) return options[0];
        return options.find((o) => o.name === value) || options[0];
    }, [options, value]);

    return (
        <div
            className='relative'
            tabIndex={0}
            onBlur={(e) => {
                const next = e.relatedTarget as HTMLElement | null;
                if (!next || !e.currentTarget.contains(next)) setOpen(false);
            }}
        >
            <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>Platform</p>
            <button
                type='button'
                className={platformBtn}
                onClick={() => setOpen((s) => !s)}
            >
                <span className='flex items-center gap-2 min-w-0'>
                    {!!selected?.icon && (
                        <img
                            src={selected.icon}
                            alt={selected.label}
                            className='h-5 w-5 rounded-sm object-contain'
                        />
                    )}
                    <span className='truncate'>{selected?.label}</span>
                </span>
                <span className='text-gray-400'>
                    <ChevronDown />
                </span>
            </button>

            {open && (
                <div className={menuBase} role='listbox'>
                    {options.map((opt) => {
                        const isActive = (value ? value === opt?.name : opt?.name === options[0]?.name);
                        return (
                            <button
                                key={opt?.name}
                                type='button'
                                role='option'
                                aria-selected={isActive}
                                className={`${menuItemBase} ${isActive ? menuItemActive : ''}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    setOpen(false);
                                    onChange(opt?.name === options[0]?.name ? undefined : opt?.name);
                                }}
                            >
                                {!!opt.icon && (
                                    <img
                                        src={opt.icon}
                                        alt={opt.label}
                                        className='h-5 w-5 rounded-sm object-contain'
                                    />
                                )}
                                <span className='truncate'>{opt.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const CostsFiltersBar = ({
    value,
    platforms,
    onChange,
    onApply,
    loading,
}: {
    value: CostsFiltersPayload;
    platforms?: PlatformOption[];
    onChange: (next: CostsFiltersPayload) => void;
    onApply: () => void;
    loading?: boolean;
}) => {
    const [pickerMode, setPickerMode] = useState<'single' | 'range'>(value.endDate ? 'range' : 'single');

    const setDates = (next: { startDate?: string; endDate?: string }) => {
        if (pickerMode === 'single') {
            onChange({ ...value, startDate: next.startDate, endDate: undefined });
            return;
        }
        onChange({ ...value, startDate: next.startDate, endDate: next.endDate });
    };

    const currentPeriodValue = useMemo(() => value.dateType || 'history', [value.dateType]);

    const platformOptions = useMemo(() => {
        const base: PlatformOption[] = [{ label: 'All platforms', value: 'all' }];
        return [...base, ...((platforms || []).filter((p) => p?.value))];
    }, [platforms]);

    return (
        <div className='w-full rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black p-4'>
            <div className='flex flex-col gap-4'>
                <div className='flex flex-col gap-1'>
                    <p className='text-sm font-medium text-black dark:text-white'>Filters</p>
                    {/* <p className='text-xs text-gray-500 dark:text-gray-400'>Pick a day or a date range, then apply.</p> */}
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-3'>
                    <div className='xl:col-span-4'>
                        <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>Date</p>
                        <DatePickerPopover
                            mode={pickerMode}
                            startDate={value.startDate}
                            endDate={value.endDate}
                            onModeChange={(m) => {
                                setPickerMode(m);
                                if (m === 'single') {
                                    onChange({ ...value, endDate: undefined });
                                } else {
                                    onChange({ ...value, startDate: value.startDate, endDate: value.endDate });
                                }
                            }}
                            onChange={setDates}
                        />
                    </div>

                    <div className='xl:col-span-2'>
                        <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>Period</p>
                        <div className={iconWrap}>
                            <select
                                value={currentPeriodValue}
                                onChange={(e) =>
                                    onChange({
                                        ...value,
                                        dateType: e.target.value === 'history' ? undefined : (e.target.value as DateKey),
                                    })
                                }
                                className={selectBase}
                            >
                                {periodOptions.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                            <div className={rightIcon}>
                                <ChevronDown />
                            </div>
                        </div>
                    </div>

                    <div className='xl:col-span-2'>
                        <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>Type</p>
                        <div className={iconWrap}>
                            <select
                                value={value.type || 'all'}
                                onChange={(e) =>
                                    onChange({
                                        ...value,
                                        type: (e.target.value === 'all' ? undefined : (e.target.value as EntityType)) || undefined,
                                    })
                                }
                                className={selectBase}
                            >
                                {typeOptions.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                            <div className={rightIcon}>
                                <ChevronDown />
                            </div>
                        </div>
                    </div>

                    <div className='xl:col-span-4'>
                        <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>Search</p>
                        <input
                            value={value.search || ''}
                            onChange={(e) => onChange({ ...value, search: e.target.value || undefined })}
                            placeholder='Client, property, scenario...'
                            className={inputBase}
                        />
                    </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-3 xl:grid-cols-12 gap-3 items-end'>
                    <div className='xl:col-span-4'>
                        <PlatformSelect
                            value={value?.provider}
                            options={platformOptions}
                            onChange={(next) => onChange({ ...value, provider: next })}
                        />
                    </div>

                    <div className='xl:col-span-4'>
                        <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>Client status</p>
                        <div className='flex flex-row items-center gap-2'>
                            <button
                                type='button'
                                onClick={() => onChange({ ...value, active: undefined })}
                                className={`${btnBase} px-3 py-2 ${value.active === undefined
                                    ? 'bg-[#00330f] text-white border-[#00330f] dark:bg-white dark:text-black dark:border-white'
                                    : 'bg-white text-black border-gray-200 dark:bg-black dark:text-white dark:border-borderColor-darkMode hover:opacity-90'
                                    }`}
                            >
                                All
                            </button>
                            <button
                                type='button'
                                onClick={() => onChange({ ...value, active: true })}
                                className={`${btnBase} px-3 py-2 ${value.active === true
                                    ? 'bg-[#00330f] text-white border-[#00330f] dark:bg-white dark:text-black dark:border-white'
                                    : 'bg-white text-black border-gray-200 dark:bg-black dark:text-white dark:border-borderColor-darkMode hover:opacity-90'
                                    }`}
                            >
                                Active
                            </button>
                            <button
                                type='button'
                                onClick={() => onChange({ ...value, active: false })}
                                className={`${btnBase} px-3 py-2 ${value.active === false
                                    ? 'bg-[#00330f] text-white border-[#00330f] dark:bg-white dark:text-black dark:border-white'
                                    : 'bg-white text-black border-gray-200 dark:bg-black dark:text-white dark:border-borderColor-darkMode hover:opacity-90'
                                    }`}
                            >
                                Inactive
                            </button>
                        </div>
                    </div>

                    {/* <div className='xl:col-span-4 flex justify-end'>
                        <button
                            type='button'
                            onClick={onApply}
                            disabled={loading}
                            className={`${btnBase} px-4 py-2 ${loading
                                ? 'opacity-60 cursor-not-allowed bg-[#00330f] text-white border-[#00330f] dark:bg-white dark:text-black dark:border-white'
                                : 'bg-[#00330f] text-white border-[#00330f] hover:opacity-90 dark:bg-white dark:text-black dark:border-white'
                                }`}
                        >
                            {loading ? 'Loading...' : 'Apply filters'}
                        </button>
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default CostsFiltersBar;